// ============================================================================
// finTituloCampos — campos do título que vêm do extrato (09/10/2026)
// ============================================================================
// Pedido da rotina financeira (09/10/2026): "quando categorizamos direto do
// EXTRATO, as informações não vão direito para o Contas a Pagar" — descrição
// com o texto cru do banco, conta só "Santander" (o dono mudou para a Stone),
// centro de custo diferente da DRE, forma de pagamento vazia, comprovante que
// não aparece. Aqui ficam as regras PURAS que os dois formulários (Contas a
// Pagar e o AccountModal do Calendário/Extrato) e a op `lancar` do servidor
// usam igual (espelho de api/_lib/fin-extrato-lancar.js — teste de paridade):
//   · FORMAS_TITULO: vocabulário ÚNICO da forma de pagamento de um título
//     (antes eram dois: 'PIX' no modal × 'pix' no Contas a Pagar — o campo
//     aparecia vazio). formaCanonica lê os valores antigos SEM regravar;
//   · formaDaClasse: a forma pela classe da linha do extrato (tarifa → Tarifa);
//   · centroCustoDaCategoria: o centro de custo É o grupo da DRE da categoria
//     ('dre:<chave>' — "Administrativo (DRE linha 5)"), derivado, nunca digitado;
//   · conta: 'caixa' | 'stone:<final>' (os antigos 'santander' e 'stone'
//     continuam legíveis: "Santander (antiga)", "Stone (conta não informada)");
//   · descricaoDoExtrato: "<favorecido limpo> — <categoria> — <unidade> <mês>"
//     (sem o "- Transferência | Pix" do banco).
// Mais o hook useContasBancarias (GET /api/fin?op=contas-bancarias: Caixa + as
// contas Stone; falha = aviso e "Stone (conta da unidade)", nunca lista vazia).
// Nada de nome de pessoa aqui.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { GRUPOS_DRE, grupoDaCategoria } from './categoriasAjuda.js';
import { finGet } from './finApi.js';

// ─── Forma de pagamento ────────────────────────────────────────────────────
export const FORMAS_TITULO = [
  'PIX', 'Boleto', 'Transferência (TED/DOC)', 'Tarifa', 'Débito Automático',
  'Cartão de Crédito', 'Cartão de Débito', 'Dinheiro', 'Cheque',
];
const normForma = (s) => String(s ?? '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const FORMA_ANTIGA = {
  pix: 'PIX', boleto: 'Boleto', transferencia: 'Transferência (TED/DOC)', ted: 'Transferência (TED/DOC)', doc: 'Transferência (TED/DOC)',
  dinheiro: 'Dinheiro', credito: 'Cartão de Crédito', debito: 'Cartão de Débito', tarifa: 'Tarifa', cheque: 'Cheque',
  'debito automatico': 'Débito Automático', 'cartao de credito': 'Cartão de Crédito', 'cartao de debito': 'Cartão de Débito',
  'transferencia (ted/doc)': 'Transferência (TED/DOC)',
};
/** Valor gravado (antigo ou novo) → a forma do vocabulário único; desconhecido = ele mesmo; vazio = ''. */
export function formaCanonica(v) {
  const s = String(v ?? '').trim();
  if (!s) return '';
  if (FORMAS_TITULO.includes(s)) return s;
  return FORMA_ANTIGA[normForma(s)] || s;
}
/** Opções do campo: as do vocabulário + o valor atual quando ele é desconhecido. */
export function opcoesForma(atual = '') {
  const c = formaCanonica(atual);
  return c && !FORMAS_TITULO.includes(c) ? [...FORMAS_TITULO, c] : [...FORMAS_TITULO];
}
/** Forma pela classe da linha do extrato. */
export function formaDaClasse(classe) {
  switch (classe) {
    case 'pix_enviado': return 'PIX';
    case 'boleto': return 'Boleto';
    case 'transferencia_enviada': return 'Transferência (TED/DOC)';
    case 'tarifa': return 'Tarifa';
    case 'outro_debito': return 'Débito Automático';
    case 'pix_recebido': return 'PIX';
    case 'transferencia_recebida': return 'Transferência (TED/DOC)';
    case 'deposito_dinheiro': return 'Dinheiro';
    default: return 'PIX';
  }
}

// ─── Centro de custo = grupo da DRE da categoria ───────────────────────────
const tituloGrupo = (chave) => GRUPOS_DRE.find(g => g.chave === chave)?.titulo || `DRE linha ${chave}`;
function itemDaCategoria(valor, fonte) {
  const v = String(valor ?? '').trim();
  if (!v || !fonte) return null;
  // catálogo do useCategoriasFin (codigoDe + porCodigo)
  if (typeof fonte.codigoDe === 'function' && fonte.porCodigo && typeof fonte.porCodigo.get === 'function') {
    if (fonte.reserva) return null; // lista de reserva não tem a linha da DRE: não inventa
    return fonte.porCodigo.get(fonte.codigoDe(v)) || null;
  }
  // plano do servidor (montarPlano: { cat: Map })
  if (fonte.cat && typeof fonte.cat.get === 'function') return fonte.cat.get(v) || null;
  // plano cru: [{ codigo, linha_dre, bloco }] ou { categorias: [...] }
  const lista = Array.isArray(fonte) ? fonte : (Array.isArray(fonte.categorias) ? fonte.categorias : []);
  return lista.find(c => c && c.codigo === v) || null;
}
/**
 * Centro de custo derivado da categoria → { valor: 'dre:<chave>', rotulo } | null.
 * Aceita (categoria, catálogoOuPlano) ou (plano, categoria) (a ordem do servidor).
 */
export function centroCustoDaCategoria(a, b) {
  const [valor, fonte] = typeof a === 'string' || a === null || a === undefined ? [a, b] : [b, a];
  const item = itemDaCategoria(valor, fonte);
  if (!item) return null;
  const chave = grupoDaCategoria(item);
  return { valor: `dre:${chave}`, rotulo: tituloGrupo(chave) };
}
/** Rótulo de um centro gravado: 'dre:5' → "Administrativo (DRE linha 5)"; antigo → '<valor> (antigo)'. */
export function rotuloCentroCusto(v) {
  const s = String(v ?? '').trim();
  if (!s) return '';
  const m = /^dre:(.+)$/.exec(s);
  if (m) return tituloGrupo(m[1]);
  return `${s.charAt(0).toUpperCase()}${s.slice(1)} (antigo)`;
}

// ─── Conta bancária ────────────────────────────────────────────────────────
export const CONTA_CAIXA = { valor: 'caixa', tipo: 'caixa', rotulo: 'Caixa (dinheiro da loja)' };
export const CONTAS_ANTIGAS = { santander: 'Santander (antiga)', stone: 'Stone (conta não informada)' };
export const RE_CONTA_STONE = /^stone:([0-9A-Za-z]{2,8})$/;
/** '<banco>:<final>' de uma conta do extrato ('stone:8648'; sem final de 4 dígitos → 'stone'). Igual ao servidor. */
export function contaBancariaDaConta(conta) {
  if (!conta) return null;
  const f = String(conta.final_conta ?? '').trim();
  return `${conta.banco || 'stone'}${/^\d{4}$/.test(f) ? `:${f}` : ''}`;
}
/** Rótulo de um valor de conta_bancaria (com a lista do servidor, quando houver). */
export function rotuloContaBancaria(valor, contas = []) {
  const v = String(valor ?? '').trim();
  if (!v) return '—';
  const achada = (contas || []).find(c => c && c.valor === v);
  if (achada?.rotulo) return achada.rotulo;
  if (v === 'caixa') return 'Caixa';
  if (CONTAS_ANTIGAS[v]) return CONTAS_ANTIGAS[v];
  const m = RE_CONTA_STONE.exec(v);
  if (m) return `Stone ••${m[1]}`;
  return v;
}
/**
 * Opções do campo Conta: Caixa + as Stone ativas (+ o valor atual, se for
 * antigo/desconhecido, só para ele continuar legível). Santander NÃO é opção nova.
 */
export function opcoesContaBancaria(contas = [], atual = '') {
  const lista = (contas || []).filter(c => c && c.valor && (c.tipo === 'caixa' || c.ativo !== false));
  const ops = lista.length ? lista.map(c => ({ valor: c.valor, rotulo: c.rotulo || rotuloContaBancaria(c.valor) }))
    : [{ valor: CONTA_CAIXA.valor, rotulo: CONTA_CAIXA.rotulo }];
  if (!ops.some(o => o.valor === 'caixa')) ops.unshift({ valor: 'caixa', rotulo: CONTA_CAIXA.rotulo });
  const a = String(atual ?? '').trim();
  if (a && !ops.some(o => o.valor === a)) ops.push({ valor: a, rotulo: rotuloContaBancaria(a, contas), antiga: true });
  return ops;
}
/** Conta padrão de um título novo da unidade (grupo → a conta dele). */
export function contaPadraoDaUnidade(unitId, info) {
  if (!unitId || !info) return '';
  return info.padrao_por_unidade?.[unitId] || '';
}

// ─── Descrição a partir do extrato ─────────────────────────────────────────
const SUFIXOS_SOCIETARIOS = /(\s+|^)(ltda|ltd|me|epp|eireli|s\.?\s?a\.?|s\/a|mei)\.?$/i;
const CONECTIVOS = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);
/** "TELEFONICA BRASIL S.A." → "Telefonica Brasil"; "Feel King Tree Barbearia Ltda" → "Feel King Tree Barbearia". */
export function limparFavorecido(nome) {
  let s = String(nome ?? '').replace(/\s+/g, ' ').trim().replace(/[.,;\-–]+$/, '').trim();
  for (let i = 0; i < 3; i++) {
    const t = s.replace(SUFIXOS_SOCIETARIOS, '').replace(/[.,;\-–]+$/, '').trim();
    if (t === s || !t) break;
    s = t;
  }
  return s.split(' ').filter(Boolean).map((p, i) => {
    const low = p.toLocaleLowerCase('pt-BR');
    if (i > 0 && CONECTIVOS.has(low)) return low;
    return low.charAt(0).toLocaleUpperCase('pt-BR') + low.slice(1);
  }).join(' ');
}
// sufixos que a Stone põe no histórico ("NOME - Transferência | Pix")
const SUFIXO_STONE = /\s*[-–]\s*(transfer[eê]ncia|pagamento|pix|devolu[cç][aã]o|tarifa|dep[oó]sito)(\s*\|\s*[^|–-]+)?\s*$/i;
/** Histórico sem o sufixo da Stone. */
export function limparHistorico(h) {
  let s = String(h ?? '').replace(/\s+/g, ' ').trim();
  for (let i = 0; i < 2; i++) {
    const t = s.replace(SUFIXO_STONE, '').trim();
    if (t === s || !t) break;
    s = t;
  }
  return s;
}
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
/** 'AAAA-MM-DD' → 'out/2026' */
export function mesAno(dia) {
  const m = /^(\d{4})-(\d{2})/.exec(String(dia ?? ''));
  return m ? `${MESES[Number(m[2]) - 1]}/${m[1]}` : '';
}
/**
 * Descrição do título a partir da linha do extrato:
 * "<favorecido limpo> — <categoria> — <unidade> <mês/aaaa>"; sem favorecido, o
 * histórico sem o sufixo da Stone; tarifa: "Tarifa Stone (<texto>)".
 */
export function descricaoDoExtrato({ favorecido = '', historico = '', classe = '', categoriaNome = '', unidadeNome = '', dia = '' } = {}) {
  const fav = limparFavorecido(favorecido);
  const hist = limparHistorico(historico);
  let base;
  if (classe === 'tarifa') base = `Tarifa Stone${hist ? ` (${hist})` : ''}`;
  else base = fav || hist || 'Débito do extrato Stone';
  // "<unidade> <mês>" só quando se sabe a unidade (sem ela, a descrição fica sem data)
  const un = String(unidadeNome || '').trim();
  const quando = un ? [un, mesAno(dia)].filter(Boolean).join(' ') : '';
  return [base, String(categoriaNome || '').trim(), quando].filter(Boolean).join(' — ').slice(0, 200);
}

// ─── Hook: contas do Financeiro (Caixa + Stone) ────────────────────────────
let cacheContas = null;
let emVooContas = null;

function validarContas(r) {
  if (!r || typeof r !== 'object' || !Array.isArray(r.contas)) throw new Error('Resposta sem a lista de contas.');
  return {
    contas: r.contas.filter(c => c && typeof c.valor === 'string'),
    padrao_por_unidade: r.padrao_por_unidade && typeof r.padrao_por_unidade === 'object' ? r.padrao_por_unidade : {},
    antigas: r.antigas && typeof r.antigas === 'object' ? r.antigas : CONTAS_ANTIGAS,
  };
}

export async function carregarContasBancariasCache({ forcar = false, buscar = () => finGet('contas-bancarias') } = {}) {
  if (cacheContas && !forcar) return cacheContas;
  if (!emVooContas || forcar) {
    emVooContas = Promise.resolve().then(() => buscar())
      .then(r => { cacheContas = validarContas(r); return cacheContas; })
      .finally(() => { emVooContas = null; });
  }
  return emVooContas;
}
export function _reiniciarContas() { cacheContas = null; emVooContas = null; }

// Reserva quando a leitura falha: Caixa + "Stone (conta da unidade)" (o valor
// antigo 'stone', legível como "conta não informada" — corrige-se depois).
export const RESERVA_CONTAS = {
  contas: [{ ...CONTA_CAIXA }, { valor: 'stone', tipo: 'stone', rotulo: 'Stone (conta da unidade)', ativo: true }],
  padrao_por_unidade: {}, antigas: CONTAS_ANTIGAS,
};

/** useContasBancarias({ enabled }) → { carregando, erro, reserva, contas, padrao_por_unidade, recarregar } */
export function useContasBancarias({ enabled = true } = {}) {
  const [estado, setEstado] = useState(() => ({ dados: cacheContas, carregando: enabled && !cacheContas, erro: '' }));
  useEffect(() => {
    if (!enabled) return undefined;
    if (cacheContas) { setEstado({ dados: cacheContas, carregando: false, erro: '' }); return undefined; }
    let vivo = true;
    setEstado(e => ({ ...e, carregando: true, erro: '' }));
    carregarContasBancariasCache()
      .then(d => { if (vivo) setEstado({ dados: d, carregando: false, erro: '' }); })
      .catch(err => { if (vivo) setEstado({ dados: null, carregando: false, erro: err?.message || 'falha de rede ou servidor' }); });
    return () => { vivo = false; };
  }, [enabled]);
  const recarregar = useCallback(async () => {
    setEstado(e => ({ ...e, carregando: true, erro: '' }));
    try {
      const d = await carregarContasBancariasCache({ forcar: true });
      setEstado({ dados: d, carregando: false, erro: '' });
    } catch (err) {
      setEstado({ dados: null, carregando: false, erro: err?.message || 'falha de rede ou servidor' });
    }
  }, []);
  return useMemo(() => {
    const d = estado.dados || (estado.erro ? RESERVA_CONTAS : null);
    return {
      carregando: enabled && estado.carregando,
      erro: estado.erro,
      reserva: !estado.dados && !!estado.erro,
      contas: d ? d.contas : [],
      padrao_por_unidade: d ? d.padrao_por_unidade : {},
      recarregar,
    };
  }, [estado, enabled, recarregar]);
}

export default {
  FORMAS_TITULO, formaCanonica, formaDaClasse, centroCustoDaCategoria, rotuloContaBancaria, descricaoDoExtrato,
  limparFavorecido, limparHistorico,
};
